import React, { useState } from "react";
import {
    Modal,
    Button,
    Row,
    Col,
    Switch,
    Space,
    Divider,
    Typography,
    notificationProvider,
    useModalForm,
    Form,
} from "@pankod/refine-antd";
import { ICompany } from "interfaces";

const { Text } = Typography;

interface ToggleFeatureFlagsModelProps {
    refetchCompanyDetail: (shouldRefetch: boolean) => void;
    companyId: string;
    companyDetail: ICompany;
}

interface FeatureFlagConfig {
    key: string;
    label: string;
    description?: string;
    dependsOn?: string[];
}

const featureFlagsConfig: FeatureFlagConfig[] = [
    {
        key: "cardExpensePolicy",
        label: "Card Expense Policy",
        description: "Enable card expense policy features"
    },
    {
        key: "kodoPayIcici2faForTxn",
        label: "KODO Pay ICICI 2FA for Transactions",
        description: "Enable two-factor authentication for ICICI transactions"
    },
    {
        key: "productFeatureApprovalRequest",
        label: "Product Feature Approval Request",
        description: "Enable product feature approval workflow"
    },
    {
        key: "dynamicWorkflow",
        label: "Dynamic Workflow",
        description: "Enable dynamic approval workflows"
    },
    {
        key: "accountingIntegration",
        label: "Accounting Integration",
        description: "Enable integration with accounting systems"
    },
    {
        key: "auditReports",
        label: "Audit Reports",
        description: "Enable audit reporting features"
    },
    {
        key: "mailbox",
        label: "Mailbox",
        description: "Enable mailbox functionality"
    },
    {
        key: "invoiceApProcessing",
        label: "Invoice AP Processing",
        description: "Enable automated invoice AP processing (requires Dynamic Workflow & Mailbox)",
        dependsOn: ["dynamicWorkflow", "mailbox"]
    },
    {
        key: "isLimitedAccessCompany",
        label: "Limited Access Company (Trial Mode)",
        description: "Enable trial/limited access mode for company"
    },
    {
        key: "isReportingAgentEnabled",
        label: "Reporting Agent",
        description: "Enable AI-powered reporting agent for company"
    },
];

export const ToggleFeatureFlagsModel: React.FC<ToggleFeatureFlagsModelProps> = ({
    refetchCompanyDetail,
    companyId,
    companyDetail
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [localFeatureFlags, setLocalFeatureFlags] = useState<Record<string, boolean>>({});
    const [currentTogglingFlag, setCurrentTogglingFlag] = useState<string | null>(null);

    // Use useModalForm similar to add-location-additional-data.model.tsx
    const {
        formProps,
        modalProps,
    } = useModalForm<any>({
        resource: "toggleCompanyFeatureFlag",
        dataProviderName: "grapghQl",
        action: "create",
        redirect: false,
        submitOnEnter: false,
        metaData: {
            companyId: companyId,
        },
        onMutationSuccess() {
            notificationProvider.open({
                message: "Success",
                description: `Feature flag toggled successfully`,
                type: "success",
            });
            refetchCompanyDetail(true);
            setCurrentTogglingFlag(null);
        },
        onMutationError(error: any) {
            // Revert the toggle if error occurs
            const flagKey = currentTogglingFlag;
            if (flagKey) {
                setLocalFeatureFlags(prev => ({
                    ...prev,
                    [flagKey]: !prev[flagKey]
                }));
            }
            notificationProvider.open({
                message: "Error",
                description: error?.message || "Failed to toggle feature flag",
                type: "error",
            });
            setCurrentTogglingFlag(null);
        }
    });

    // Don't use the modal from useModalForm, manage our own
    const isLoading = modalProps.confirmLoading || false;

    const showModal = () => {
        // Initialize local state with current feature flags
        const currentFlags: Record<string, boolean> = {};
        featureFlagsConfig.forEach(config => {
            currentFlags[config.key] = companyDetail.featureFlags?.[config.key] || false;
        });
        setLocalFeatureFlags(currentFlags);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setLocalFeatureFlags({});
    };

    const handleToggle = (featureFlagKey: string, checked: boolean) => {
        // Update local state immediately for UI responsiveness
        setLocalFeatureFlags(prev => ({
            ...prev,
            [featureFlagKey]: checked
        }));

        setCurrentTogglingFlag(featureFlagKey);

        // Set the form values and submit (similar to add-location pattern)
        formProps.form?.setFieldsValue({
            featureFlagKey: featureFlagKey,
        });

        // Submit the form which will trigger the mutation
        formProps.form?.submit();
    };

    const isFeatureFlagDisabled = (config: FeatureFlagConfig): boolean => {
        if (!config.dependsOn || config.dependsOn.length === 0) {
            return false;
        }
        // Check if all dependencies are enabled
        return !config.dependsOn.every(depKey =>
            localFeatureFlags[depKey] || companyDetail.featureFlags?.[depKey]
        );
    };

    const FeatureFlagToggleItem = ({ config }: { config: FeatureFlagConfig }) => {
        const currentValue = localFeatureFlags[config.key] !== undefined
            ? localFeatureFlags[config.key]
            : (companyDetail.featureFlags?.[config.key] || false);

        const disabled = isFeatureFlagDisabled(config);

        return (
            <Row
                justify="space-between"
                align="middle"
                style={{
                    paddingTop: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid #f0f0f0'
                }}
            >
                <Col span={18}>
                    <Space direction="vertical" size={0}>
                        <Text strong>{config.label}</Text>
                        {config.description && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {config.description}
                            </Text>
                        )}
                        {disabled && (
                            <Text type="warning" style={{ fontSize: '11px' }}>
                                Requires: {config.dependsOn?.join(', ')}
                            </Text>
                        )}
                    </Space>
                </Col>
                <Col span={6} style={{ textAlign: 'right' }}>
                    <Switch
                        checked={currentValue}
                        onChange={(checked) => handleToggle(config.key, checked)}
                        loading={isLoading && currentTogglingFlag === config.key}
                        disabled={disabled || (isLoading && currentTogglingFlag === config.key)}
                    />
                </Col>
            </Row>
        );
    };

    return (
        <>
            <Button
                type="default"
                onClick={showModal}
            >
                Manage Feature Flags
            </Button>

            <Modal
                title={`Feature Flags - ${companyDetail.registeredName}`}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="close" onClick={handleCancel}>
                        Close
                    </Button>
                ]}
                width={700}
                centered
            >
                {/* Hidden form to handle mutations */}
                <Form {...formProps} layout="vertical">
                    {/* Hidden field for featureFlagKey - will be set on toggle */}
                    <Form.Item name="featureFlagKey" hidden>
                        <input type="hidden" />
                    </Form.Item>
                </Form>

                {/* Display section for feature flags */}
                <div>
                    <Divider orientation="left">Company Feature Flags</Divider>
                    {featureFlagsConfig.map((config) => (
                        <FeatureFlagToggleItem key={config.key} config={config} />
                    ))}
                </div>
            </Modal>
        </>
    );
};
